import { Button, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect } from 'react';
import Card from "../../common/card/Card";
import useModal from "../../common/hooks/useModal";
import Modal from "../../common/modal/Modal";
import GenericTable from "../../common/table/GenericTable";
import BreadcrumbsBar from "../../layouts/breadcrumb/BreadcrumbsBar";
import { getAllCampaigns, getGeneratedLinkFromAPI, getMLinkReports } from "../../services/mlinkService";
import styles from "./MLinkIntegration.module.scss";

const MLinkIntegration = () => {

    const [campaigns, setCampaigns] = React.useState([]);
    const [selectedInfId, setSelectedInfId] = React.useState(null);
    const [reports, setReports] = React.useState([]);
    const [reportFilters, setReportFilters] = React.useState({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const {isModalOpen, toggleModal, modalContent, setModalContent} = useModal();
    const productColumns = {};
    productColumns.visibility = {}
    productColumns.data = [
        {
            accessorKey: 'name',
            header: "Product Name",
        }, {
            accessorKey: 'image',
            header: "Product Name",
            Cell: (cell) => (<img src={cell.row.original.image} alt={cell.row.original.name} width={100}/>)
        }
    ];

    const reportsColumns = {};
    reportsColumns.visibility = {};
    reportsColumns.data = [
        {
            accessorKey: 'campaignID',
            header: "Campaign ID",
        },
        {
            accessorKey: 'influencerID',
            header: "Influencer ID",
        },
        {
            accessorKey: 'name',
            header: "Campaign Name",
        },
        {
            accessorKey: 'endDate',
            header: "End Date",
        },
        {
            accessorKey: 'totalClicks',
            header: "Total Clicks",
        },
        {
            accessorKey: 'totalSales',
            header: "Total Sales",
        },
        {
            accessorKey: 'brandCampaignCommisionAmount',
            header: "Brand Commission Amount",
            Cell: (cell) => (
                <div className={styles.commissionAmount}>₺{cell.row.original.brandCampaignCommisionAmount?.toFixed(2) || '0.00'}</div>
            )
        },
        {
            accessorKey: 'influencerCommisionAmount',
            header: "Influencer Commission Amount",
            Cell: (cell) => (
                <div className={styles.commissionAmount}>₺{cell.row.original.influencerCommisionAmount?.toFixed(2) || '0.00'}</div>
            )
        },
        {
            accessorKey: 'agencyCommissionAmount',
            header: "Agency Commission Amount",
            Cell: (cell) => (
                <div className={styles.commissionAmount}>₺{cell.row.original.agencyCommissionAmount?.toFixed(2) || '0.00'}</div>
            )
        }
    ];

    const campaignColumns = {};
    campaignColumns.visibility = {};
    campaignColumns.data = [
        {
            accessorKey: 'Actions',
            header: "Actions",
            Cell: (cell) => (
                <div className={styles.generateLinkContainer}>
                    <TextField
                        id="outlined-basic"
                        name="titleEn"
                        label={"Influencer ID"}
                        value={selectedInfId}
                        variant="outlined"
                        onChange={handleInputChange}
                    />
                    <Button variant="contained" color="primary" size="small" onClick={(e) => {
                        generateCampaignLink(cell.row.original.id);
                    }
                    }>Generate Link</Button>
                </div>
            )
        },
        {
            accessorKey: 'id',
            header: "Campaign ID",
        }, {
            accessorKey: 'name',
            header: "Campaign Name",
        }, {
            accessorKey: 'brandCampaignCommissionRate',
            header: "Brand Commission Rate",
            Cell: (cell) => (<div>%{cell.row.original.brandCampaignCommissionRate}</div>)
        }, {
            accessorKey: 'influencerCommissionRate',
            header: "Influencer Commission Rate",
            Cell: (cell) => (<div>%{cell.row.original.influencerCommissionRate}</div>)
        }, {
            accessorKey: 'otherCostsRate',
            header: "Other Costs Rate",
            Cell: (cell) => (<div>%{cell.row.original.otherCostsRate}</div>)
        }, {
            accessorKey: 'endDate',
            header: "End Date",
            Cell: (cell) => (<div> {cell.row.original.endDate}</div>)
        },
    ];


    const handleInputChange = (e) => {
        const {value} = e.target;
        setSelectedInfId(value);
    }

    const getCampaigns = async () => {
        const response = await getAllCampaigns()
        if (response.status === 200) {
            setCampaigns(response.data);
        } else {
            console.error("Failed to fetch campaigns");
        }
    }


    const getGeneratedLink = async (campaignId) => {
        const payload = {
            "influencerID": selectedInfId,
            "influencerName": selectedInfId,
            "campaignID": campaignId
        }
        const response = await getGeneratedLinkFromAPI(payload);

        const urlData = response.data;
        if (!!urlData && !!urlData.url) {
            setModalContent({
                title: "Generated Link",
                content: <div>
                    <p>Generated Link: {urlData.url}</p>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={(e) => {
                            navigator.clipboard.writeText(urlData.url);
                        }
                        }>Copy Link</Button>
                    <p>Campaign Name: {urlData.name}</p>
                    <p>End Date: {urlData.endDate}</p>
                </div>
            })
        } else {
            setModalContent({
                title: "Error",
                content: <div>
                    <p>{urlData.message}</p>
                </div>
            })
        }
        toggleModal();

    }


    const generateCampaignLink = async (campaignID) => {

        await getGeneratedLink(campaignID);
    }

    const fetchReports = async (filters = {}) => {
        const params = {
            ...filters
        };
        
        // Remove empty filters
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });
        
        return await getMLinkReports(params);
    };

    const refreshReports = async () => {
        const response = await fetchReports(reportFilters);
        if (response.status === 200 && response.data && response.data.length > 0) {
            // Sort by total sales (descending) then by total clicks (descending)
            const sortedData = response.data.sort((a, b) => {
                // First sort by total sales (descending)
                if (b.totalSales !== a.totalSales) {
                    return b.totalSales - a.totalSales;
                }
                // If total sales are equal, sort by total clicks (descending)
                return b.totalClicks - a.totalClicks;
            });
            setReports(sortedData);
        } else {
            setReports([]);
        }
    };

    const viewAllReports = useCallback(async () => {
        const response = await fetchReports();
        
        if (response.status === 200 && response.data && response.data.length > 0) {
            // Sort by total sales (descending) then by total clicks (descending)
            const sortedData = response.data.sort((a, b) => {
                // First sort by total sales (descending)
                if (b.totalSales !== a.totalSales) {
                    return b.totalSales - a.totalSales;
                }
                // If total sales are equal, sort by total clicks (descending)
                return b.totalClicks - a.totalClicks;
            });
            setReports(sortedData);
        } else {
            setReports([]);
        }
    }, []);

    const clearReports = () => {
        setReports([]);
        setReportFilters({
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
        });
    }

    const expandProducts = (cell) => {
        console.log(cell);
        return (
            <GenericTable
                outputColumns={productColumns}
                tableData={cell.row.original.products}
                rowCount={cell.row.original.products.length}
                tableExpandPadding={0}
                headerBgColor={"#E74C3C"}
            ></GenericTable>
        )
    }

    useEffect(() => {
        const loadData = async () => {
            try {
                await getCampaigns();
                setTimeout(() => {
                    viewAllReports();
                }, 1000);
            } catch (error) {
                console.error("Error loading campaigns:", error);
            }
        };
        loadData();
    }, [viewAllReports]);

    return (<div>
        <BreadcrumbsBar/>
        <Card title="Mlink Campaigns">
            <div className="container">
                <div style={{ marginBottom: '16px' }}>
                    <Typography variant="h6">Campaigns</Typography>
                </div>
                {/* Add your integration logic and components here */}
                {campaigns && campaigns.length > 0 && <GenericTable
                    outputColumns={campaignColumns}
                    tableData={campaigns}
                    rowCount={campaigns.length}
                    expand={expandProducts}
                    tableExpandPadding={0}
                    enablePagination={true}
                />}
            </div>
        </Card>

        {/* Reports Section */}
        {reports && reports.length > 0 && (
            <div className={styles.reportsSection}>
                <Card title="MLink Reports" className={styles.reportsCard}>
                    <div className="container">
                    <div className={styles.filterSection}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Typography variant="h6">Filter Reports</Typography>
                            <Button 
                                variant="outlined" 
                                color="secondary" 
                                onClick={clearReports}
                                size="small"
                            >
                                Clear Reports
                            </Button>
                        </div>
                        <div className={styles.filterControls}>
                            <TextField
                                label="Start Date"
                                type="date"
                                value={reportFilters.startDate}
                                onChange={(e) => setReportFilters({...reportFilters, startDate: e.target.value})}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                value={reportFilters.endDate}
                                onChange={(e) => setReportFilters({...reportFilters, endDate: e.target.value})}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button 
                                variant="contained" 
                                onClick={refreshReports}
                                size="small"
                            >
                                Apply Filters
                            </Button>
                        </div>
                    </div>
                    <div className={styles.reportsTable}>
                        <GenericTable
                            outputColumns={reportsColumns}
                            tableData={reports}
                            rowCount={reports.length}
                            enablePagination={true}
                            headerBgColor={"#3498DB"}
                        />
                    </div>
                    </div>
                </Card>
            </div>
        )}

        <Modal
            isOpen={isModalOpen}
            title={modalContent.title}
            content={modalContent.content}
            onCancelation={toggleModal}
        />
    </div>);
};

export default MLinkIntegration;
